import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { Header } from "components/DisplayBox";
import { H3 } from "components/Text";
import { RoleAttachmentsList } from "components/Missions/Roles";
import { MODAL_SIZE, ModalForm } from "components/Modal";
import { SIZE, TYPE } from "components/Form";
import { ADD_MISSION_ROLE_ATTACHMENT } from "graphql/mutations";
import { useToast, MUTATION_TYPE, useMissionRole } from "hooks";
import { ICON_TYPE } from "components/Icon";
import { EditLink } from "components/Links";
import { EditButton } from "components/Buttons";

/**
 * RoleAttachments
 *
 * @param {Object}     missionRole
 * @param {Boolean}    loading
 */
const RoleAttachments = ({ missionRole, loading, ...props }) => {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const attachmentsExists = !!missionRole?.attachments?.length;
  const { handleRemoveAttachment } = useMissionRole({ id: missionRole?.id, skip: true });

  const handleClose = () => {
    setShowForm(false);
  };

  const handleComplete = () => {
    handleClose();
    toast.success(<FormattedMessage id="RoleAttachments.AddAttachmentForm.CreateSuccess" />);
  };

  const handleError = () => {
    toast.error(<FormattedMessage id="RoleAttachments.AddAttachmentForm.CreateError" />);
  };

  const rowOptions = (attachment) => [
    {
      label: <FormattedMessage id="Global.Remove" />,
      icon: ICON_TYPE.trash,
      onClick: () => handleRemoveAttachment(attachment?.id),
    },
  ];

  return (
    <>
      <Header {...props}>
        <H3>
          <FormattedMessage id="Missions.MissionRole.Attachments" />
        </H3>
        {attachmentsExists && (
          <EditLink onClick={() => setShowForm(true)}>
            <FormattedMessage id="RoleAttachments.EditButtonText" />
          </EditLink>
        )}
      </Header>
      {attachmentsExists ? (
        <RoleAttachmentsList
          data={missionRole?.attachments}
          loading={loading}
          rowOptions={rowOptions}
          disablePagination
        />
      ) : (
        <EditButton onClick={() => setShowForm(true)}>
          <FormattedMessage id="RoleAttachments.EditButtonText" />
        </EditButton>
      )}
      {showForm && (
        <ModalForm
          show={showForm}
          onClose={handleClose}
          onCompleted={handleComplete}
          onError={handleError}
          updateId={missionRole?.id}
          mutationType={MUTATION_TYPE.update}
          {...FORM_SETTINGS}
        />
      )}
    </>
  );
};

const FORM_DATA = [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        type: TYPE.fileUpload,
        size: SIZE.xlarge,
        properties: {
          name: "attachment",
          uploadMessageId: "RoleAttachments.EditButtonText",
        },
      },
    ],
  },
];

const FORM_SETTINGS = {
  numberOfColumns: 1,
  size: MODAL_SIZE.medium,
  data: FORM_DATA,
  title: <FormattedMessage id="RoleAttachments.AddAttachmentForm.Title" />,
  description: <FormattedMessage id="RoleAttachments.AddAttachmentForm.Description" />,
  mutationData: {
    mutationName: {
      update: ADD_MISSION_ROLE_ATTACHMENT,
    },
  },
};

RoleAttachments.propTypes = {
  missionRole: PropTypes.object,
  loading: PropTypes.bool,
};

export default RoleAttachments;
